import { useState } from 'react';
import { MapPin, MoreHorizontal, Pencil, FolderInput, Archive, ArchiveRestore, Trash2, RotateCcw, ExternalLink, Flame } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useProjectsHubStore, isTrashed, isArchived, TRASH_DAYS } from '../../stores/useProjectsHubStore';
import { StatusPill, HEALTH, fmtDate, daysSince } from './projectUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLS = 'md:grid-cols-[28px_minmax(0,1.6fr)_minmax(0,1.2fr)_64px_104px_120px_112px_40px]';

const Thumb = ({ project }) => {
  const fid = project.lots_preview?.[0]?.file_id;
  const [broken, setBroken] = useState(false);
  return (
    <div className="w-10 h-10 rounded-lg shrink-0 overflow-hidden border border-[var(--c-border)] bg-[var(--c-surface-2)] flex items-center justify-center">
      {fid && !broken ? (
        <img src={`${API}/files/${fid}/download`} alt="" className="w-full h-full object-cover" crossOrigin="use-credentials" onError={() => setBroken(true)} />
      ) : (
        <span className="text-[13px] font-semibold text-[var(--c-text-faint)]" style={{ fontFamily: 'Outfit, sans-serif' }}>{(project.name || '?').charAt(0).toUpperCase()}</span>
      )}
    </div>
  );
};

const RowMenu = ({ project, onRename, onMove, onAct, onOpen }) => {
  const trashed = isTrashed(project);
  const archived = isArchived(project);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <button type="button" className="w-8 h-8 rounded-md flex items-center justify-center text-[var(--c-text-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] transition-colors" data-testid={`project-menu-${project.project_id}`} aria-label="Actions">
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
        {trashed ? (
          <>
            <DropdownMenuItem onClick={() => onAct('restore')} data-testid={`restore-project-${project.project_id}`}><RotateCcw className="w-4 h-4 mr-2" />Restaurer</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onAct('delete')} className="text-red-600 focus:text-red-600" data-testid={`delete-project-${project.project_id}`}><Flame className="w-4 h-4 mr-2" />Supprimer définitivement</DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem onClick={onOpen} data-testid={`open-project-${project.project_id}`}><ExternalLink className="w-4 h-4 mr-2" />Ouvrir</DropdownMenuItem>
            <DropdownMenuItem onClick={onRename} data-testid={`edit-project-${project.project_id}`}><Pencil className="w-4 h-4 mr-2" />Renommer</DropdownMenuItem>
            <DropdownMenuItem onClick={onMove} data-testid={`move-project-${project.project_id}`}><FolderInput className="w-4 h-4 mr-2" />Déplacer vers un dossier</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAct(archived ? 'unarchive' : 'archive')} data-testid={`archive-project-${project.project_id}`}>
              {archived ? <ArchiveRestore className="w-4 h-4 mr-2" /> : <Archive className="w-4 h-4 mr-2" />}{archived ? 'Désarchiver' : 'Archiver'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onAct('trash')} className="text-red-600 focus:text-red-600" data-testid={`trash-project-${project.project_id}`}><Trash2 className="w-4 h-4 mr-2" />Mettre à la corbeille</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ProjectsTable = ({ projects, onRename, onMove, onAct, onOpen }) => {
  const { selectedId, select, checked, toggleCheck, setChecked, setDragIds, filter } = useProjectsHubStore();
  const anyChecked = checked.length > 0;
  const allChecked = projects.length > 0 && projects.every((p) => checked.includes(p.project_id));
  const trashView = filter === 'trash';

  return (
    <div className="rounded-2xl border border-[var(--c-border)] bg-[var(--c-surface-0)] overflow-hidden" data-testid="projects-list">
      <div className={`hidden md:grid ${COLS} gap-3 items-center px-5 h-10 text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--c-text-faint)] border-b border-[var(--c-border)]`}>
        <span className="flex items-center"><Checkbox checked={allChecked} onCheckedChange={(v) => setChecked(v ? projects.map((p) => p.project_id) : [])} data-testid="projects-check-all" aria-label="Tout sélectionner" /></span>
        <span>Projet</span>
        <span>Localisation</span>
        <span className="text-right">Lots</span>
        <span>Statut</span>
        <span>{trashView ? 'Purge' : 'Lots générés'}</span>
        <span>{trashView ? 'Supprimé' : 'Modifié'}</span>
        <span />
      </div>

      {projects.map((p, i) => {
        const selected = selectedId === p.project_id;
        const isChecked = checked.includes(p.project_id);
        const h = HEALTH[p.health_status] || HEALTH.gray;
        const meta = p.health_meta || {};
        const pct = meta.total_lots ? Math.round((100 * (meta.lots_with_gen || 0)) / meta.total_lots) : 0;
        const left = p.deleted_at ? Math.max(0, TRASH_DAYS - daysSince(p.deleted_at)) : null;
        return (
          <div
            key={p.project_id}
            draggable={!trashView}
            onDragStart={(e) => { const ids = isChecked ? checked : [p.project_id]; setDragIds(ids); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', ids.join(',')); }}
            onDragEnd={() => setDragIds([])}
            onClick={() => select(p.project_id)}
            data-testid={`project-row-${p.project_id}`}
            data-selected={selected ? '1' : '0'}
            style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
            className={`hud-fade-in group relative grid grid-cols-[28px_minmax(0,1fr)_40px] ${COLS} gap-3 items-center px-5 min-h-[60px] py-2.5 cursor-pointer border-b border-[var(--c-border)] last:border-b-0 transition-colors
              ${selected ? 'bg-[var(--c-gold-soft)]' : isChecked ? 'bg-[var(--c-surface-1)]' : 'hover:bg-[var(--c-surface-1)]'}`}
          >
            {selected && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-[var(--c-gold)]" />}
            <span className={`flex items-center transition-opacity ${anyChecked || isChecked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} onClick={(e) => e.stopPropagation()}>
              <Checkbox checked={isChecked} onCheckedChange={() => toggleCheck(p.project_id)} data-testid={`project-checkbox-${p.project_id}`} aria-label={`Sélectionner ${p.name}`} />
            </span>
            <div className="flex items-center gap-3 min-w-0">
              <Thumb project={p} />
              <div className="min-w-0">
                <p className="text-[14px] font-semibold text-[var(--c-text)] truncate leading-5" data-testid={`project-name-${p.project_id}`}>{p.name}</p>
                <p className="md:hidden text-[12px] text-[var(--c-text-muted)] truncate">{p.address || '—'}</p>
                {isArchived(p) && <span className="inline-flex mt-0.5 px-1.5 py-px rounded border border-[var(--c-border)] text-[10px] uppercase tracking-wider text-[var(--c-text-muted)]">Archivé</span>}
              </div>
            </div>
            <div className="hidden md:flex items-center gap-1.5 min-w-0 text-[12.5px] text-[var(--c-text-2)]">
              <MapPin className="w-3.5 h-3.5 shrink-0 text-[var(--c-text-faint)]" />
              <span className="truncate">{p.address || (Array.isArray(p.coords) && p.coords.length === 2 ? `${p.coords[0].toFixed(3)}, ${p.coords[1].toFixed(3)}` : '—')}</span>
            </div>
            <span className="hidden md:block text-[13px] font-medium tabular-nums text-right text-[var(--c-text)]" style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }} data-testid={`project-row-lots-${p.project_id}`}>{p.lots_count || 0}</span>
            <span className="hidden md:block"><StatusPill status={p.status} /></span>
            {trashView ? (
              <span className="hidden md:block text-[12px] text-red-600 tabular-nums" data-testid={`project-purge-${p.project_id}`}>dans {left} j</span>
            ) : (
              <div className="hidden md:flex items-center gap-2 min-w-0" title={`${meta.lots_with_gen || 0}/${meta.total_lots || 0} lots générés`}>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${h.dot}`} />
                <div className="flex-1 h-1 rounded-full bg-[var(--c-surface-3)] overflow-hidden max-w-[64px]"><div className="h-full rounded-full bg-[var(--c-gold)]" style={{ width: `${pct}%` }} /></div>
                <span className="text-[11px] text-[var(--c-text-muted)] tabular-nums w-7">{pct}%</span>
              </div>
            )}
            <span className="hidden md:block text-[12px] text-[var(--c-text-muted)] tabular-nums">{fmtDate(trashView ? p.deleted_at : p.updated_at)}</span>
            <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
              <RowMenu project={p} onRename={() => onRename(p)} onMove={() => onMove([p.project_id])} onAct={(a) => onAct([p.project_id], a)} onOpen={() => onOpen(p)} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
